import { pxVar } from '../../../mixins/number-with-unit/number-with-unit.css-vars';
import type { ColorCssResolver } from '../../form/styles/themed-button.css-vars';
import type {
    FormCssVars,
    FormInputSettingsLike,
    FormRootThemeTokens,
    FormWidgetLike,
} from '../../form/styles/form.css-vars';
import { getFormRootCssVars } from '../../form/styles/form.css-vars';

/**
 * CSS custom properties for the PAYMENT-FORM widget — the LESS twin of the
 * former JSS `getBaseFormStyles(data, Widgets.PAYMENT_FORM, …)` slice.
 *
 * The payment form shares its entire INLINE styling surface (root, input
 * theming, sizes, alignment, submit button) with the subscription-form widget,
 * so those variable builders are re-exported verbatim from
 * `form.css-vars.ts` (one source of truth — both operate on the same
 * `FormInputSettings` / `FormButtonSettings` shapes). Payment-form diverges only
 * in the MODAL: it has no separate modal-input theming or chat-bots (those are
 * `Widgets.FORM`-only branches of `getBaseFormStyles`); its modal is the regular
 * inline form rendered inside a themed `.payment-form-modal-container`
 * (background / radius / padding / close-button colour). That payment-only slice
 * lives here.
 *
 * Variables cascade from the form root exactly as in the form widget; a `null`
 * value removes the property (mirroring the omitted-key behaviour of the old JSS
 * object); per-device overrides use `var(--x-mobile, var(--x))` fallback chains.
 */
export type { FormCssVars } from '../../form/styles/form.css-vars';

// Inline surface — identical to the form widget, re-exported as-is.
export {
    getFormRootCssVars,
    getFormRootCssClasses,
    getFormAlignCssVars,
    getFormInputCssVars,
    getFormInputCssClasses,
    getFormSizeCssVars,
    getFormButtonCssVars,
    getFormButtonCssClasses,
} from '../../form/styles/form.css-vars';

/**
 * Root variables for the payment-form root div (the `form` + `formPosition` JSS
 * keys) — the form widget's own root builder: both read only `type` and the
 * rotation in `position`. Zero placement drops its own rotation — the zero host
 * applies it.
 */
export function getPaymentFormRootCssVars(widget: FormWidgetLike, theme: FormRootThemeTokens): FormCssVars {
    return getFormRootCssVars(widget, theme);
}

/**
 * The payment-form description block (`paymentFormDescription` JSS key) — the
 * product name / price text. All three (root, `h2`, `p`) take the input
 * `textColor`.
 */
export function getPaymentDescriptionCssVars(
    inputSettings: FormInputSettingsLike,
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        '--payment-description-color': resolveColor(inputSettings.textColor),
    };
}

/**
 * Modal-container theming (the `modalBodyStyles['&.payment-form-modal-container']`
 * JSS key). Defaults mirror `getBaseFormStyles`: bg `#fbfcfc`, radius 6, padding
 * 45. The close button takes the input `textColor`.
 */
export function getPaymentModalCssVars(
    inputSettings: FormInputSettingsLike,
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        '--payment-modal-bg': resolveColor(inputSettings.modalBackgroundColor ?? '#fbfcfc'),
        '--payment-modal-radius': pxVar(inputSettings.modalBorderRadius ?? 6),
        '--payment-modal-padding': pxVar(inputSettings.modalPadding ?? 45),
        '--payment-modal-close-color': resolveColor(inputSettings.textColor),
    };
}
